import React, { useState } from 'react';
import { 
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  Textarea,
  Card,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  addToast
} from '@heroui/react';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { createUser, updateUser, deleteUser } from '../../services/api';
import UserAvatar from '../UserAvatar/UserAvatar';
import './UserManagement.css';

const UserManagement = ({ users, selectedUserId, onSelectUser, onUserCreated, onUserUpdated, onUserDeleted }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    externalId: '',
    name: '',
    displayName: '',
    password: '',
    prescription: []
  });
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.displayName?.toLowerCase().includes(searchLower) ||
      String(user.externalId).includes(searchLower) ||
      String(user.id).includes(searchLower)
    );
  });

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      externalId: '',
      name: '',
      displayName: '',
      password: '',
      prescription: []
    });
    onOpen();
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      externalId: user.externalId || '',
      name: user.name || '',
      displayName: user.displayName || '',
      password: '', // Don't pre-fill password for security
      prescription: user.prescription || []
    });
    onOpen();
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);
      
      // Close modal
      onDeleteClose();
      setUserToDelete(null);
      
      // Remove from list by calling parent callback
      if (onUserDeleted) {
        onUserDeleted(userToDelete.id);
      }
      
      // Show success message
      addToast({
        title: "User Deleted",
        description: `User "${userToDelete.name}" deleted successfully!`,
        color: "success"
      });
      
    } catch (error) {
      console.error('Error deleting user:', error);
      addToast({
        title: "Delete Error",
        description: "Failed to delete user. Please try again.",
        color: "danger"
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      addToast({
        title: "Validation Error",
        description: "Name is required",
        color: "danger"
      });
      return;
    }
    if (!formData.displayName.trim()) {
      addToast({
        title: "Validation Error",
        description: "Display Name is required",
        color: "danger"
      });
      return;
    }
    if (!formData.externalId) {
      addToast({
        title: "Validation Error",
        description: "External ID is required",
        color: "danger"
      });
      return;
    }
    if (!editingUser && !formData.password.trim()) {
      addToast({
        title: "Validation Error",
        description: "Password is required for new users",
        color: "danger"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingUser) {
        // Update existing user
        const response = await updateUser(editingUser.id, formData);
        const updatedUser = response.data;
        
        // Close modal
        onClose();
        
        // Update user in parent list
        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }
        
        addToast({
          title: "User Updated",
          description: `User "${updatedUser.name}" updated successfully!`,
          color: "success"
        });
      } else {
        // Create new user
        const response = await createUser(formData);
        const newUser = response.data;
        
        // Close modal
        onClose();
        
        // Reset form
        setFormData({
          externalId: '',
          name: '',
          displayName: '',
          password: '',
          prescription: []
        });
        
        // Add new user to parent list
        if (onUserCreated) {
          onUserCreated(newUser);
        }
        
        addToast({
          title: "User Created",
          description: `User "${newUser.name}" created successfully!`,
          color: "success"
        });
      }
      
    } catch (error) {
      console.error('Error saving user:', error);
      addToast({
        title: "Save Error",
        description: "Failed to save user. Please try again.",
        color: "danger"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setEditingUser(null);
    setFormData({
      externalId: '',
      name: '',
      displayName: '',
      password: '',
      prescription: []
    });
  };

  return (
    <div className="user-management-container">
      {/* Header with Search and Add Button */}
      <div className="user-management-header">
        <div className="search-container">
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        
        <Button
          color="primary"
          variant="solid"
          size="sm"
          onPress={handleAddUser}
          className="add-user-btn"
          startContent={<PlusIcon className="h-4 w-4" />}
        >
          Add User
        </Button>
      </div>

      {/* User List */}
      <div className="user-list">
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3>No users found</h3>
            <p>Try adjusting your search or add your first user</p>
            <Button
              color="primary"
              variant="bordered"
              onPress={handleAddUser}
              startContent={<PlusIcon className="h-4 w-4" />}
            >
              Add Your First User
            </Button>
          </div>
        ) : (
          <div className="user-grid">
            {filteredUsers.map(user => (
              <Card 
                key={user.id}
                className={`user-card ${user.id === selectedUserId ? 'selected' : ''}`}
                isPressable
                onPress={() => onSelectUser(user.id)}
              >
                <CardBody className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <UserAvatar 
                        user={user} 
                        size="lg" 
                        showBorder={true}
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {user.displayName || user.name || 'Unnamed User'}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          ID: {user.id} | External: {user.externalId}
                        </p>
                      </div>
                    </div>
                    
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <EllipsisVerticalIcon className="h-4 w-4" />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu aria-label="User actions">
                        <DropdownItem
                          key="edit"
                          startContent={<PencilIcon className="h-4 w-4" />}
                          onPress={() => handleEditUser(user)}
                        >
                          Edit User
                        </DropdownItem>
                        <DropdownItem
                          key="delete"
                          className="text-danger"
                          color="danger"
                          startContent={<TrashIcon className="h-4 w-4" />}
                          onPress={() => handleDeleteClick(user)}
                        >
                          Delete User
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Username:</span>
                      <span className="text-gray-700 dark:text-gray-300 truncate max-w-[150px]">
                        {user.name || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">External ID:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {user.externalId || 'Not provided'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Prescriptions:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {user.prescription?.length || 0} items
                      </span>
                    </div>
                    {user.profilePicturePath && (
                      <div className="mt-2">
                        <Chip size="sm" color="success" variant="flat">
                          Has Profile Picture
                        </Chip>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit User Modal */}
      <Modal isOpen={isOpen} onClose={handleCancel} size="2xl">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <UserIcon className="w-5 h-5 text-blue-500" />
              <h2 className="modal-title">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
            </div>
            <p className="text-sm text-gray-500">
              {editingUser ? 'Update user information' : 'Enter user information for smart suit'}
            </p>
          </ModalHeader>
          <ModalBody>
            <div className="add-user-form">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="External ID"
                    type="number"
                    placeholder="Enter external ID"
                    value={formData.externalId}
                    onChange={(e) => handleInputChange('externalId', parseInt(e.target.value) || '')}
                    isRequired
                    description="Unique identifier from external system"
                  />
                  
                  <Input
                    label="Username"
                    placeholder="Enter username"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    isRequired
                    description="Unique username for login"
                  />
                </div>
                
                <Input
                  label="Display Name"
                  placeholder="Enter display name"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  isRequired
                  description="Full name to display in the interface"
                />
                
                <Input
                  label={editingUser ? "New Password (Optional)" : "Password"}
                  type="password"
                  placeholder={editingUser ? "Enter new password (leave blank to keep current)" : "Enter password for login"}
                  value={formData.password || ''}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  isRequired={!editingUser}
                  description={editingUser ? "Leave blank to keep current password" : "Password required for user login"}
                />
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <UserIcon className="w-4 h-4 text-blue-600" />
                    <h4 className="text-sm font-medium text-blue-800">Smart Suit User</h4>
                  </div>
                  <p className="text-sm text-blue-700">
                    This user will be able to perform exercises using the smart suit system. 
                    Prescriptions will be managed separately and can be added later.
                  </p>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={handleCancel}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onPress={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!formData.name.trim() || !formData.displayName.trim() || !formData.externalId}
            >
              {isSubmitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <TrashIcon className="w-5 h-5 text-red-500" />
              <h2 className="modal-title">Delete User</h2>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>"{userToDelete?.name}"</strong>?
              </p>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center gap-2">
                  <TrashIcon className="w-4 h-4 text-red-600" />
                  <p className="text-sm text-red-700 font-medium">Warning</p>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  This action cannot be undone. All user data and exercise history will be permanently deleted.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="default" variant="light" onPress={onDeleteClose}>
              Cancel
            </Button>
            <Button 
              color="danger" 
              onPress={handleDeleteConfirm}
              startContent={<TrashIcon className="h-4 w-4" />}
            >
              Delete User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default UserManagement;
